import {
    ApiErrorResponseWithCode,
    getCookie,
    requestHtml,
    requestJson,
} from '@vfde-sails/utils';
import {
    JarvisAddressValidationRequest,
    JarvisAddressValidationResponse,
    JarvisAlternativeRequest,
    JarvisAlternativeResponse,
    JarvisAvcRequest,
    JarvisAvcResponse,
    JarvisProduct,
    ProductDetailsRequest,
    ProductDetailsResponse,
} from '../interface';
import {
    getAllTariffIds,
    getCMSConfig,
} from './cms';
import { getUrl } from './url';
import {
    Cookies,
    Domains,
    Endpoints,
    ProductType,
} from '../enum';
import { Address } from '@vfde-sails/avc-core';

const defaultOptions: RequestInit = {
    headers: {
        'Content-Type': 'application/json',
        'X-Api-Key': 'KSZZFe5MPWBLc3cLUPs2zW5at1w7xQyg',
    },
};
/*
* @returns True if the current environment is ST
*/
/**
 *
 */
export const isStagingEnv = () => window.location.origin === Domains.ST;

/**
 * Get tariff details from Jarvis
 * getTariffs
 *
 * @throws {ApiErrorResponseWithCode} - if the server returns with error
 * @returns A Promise which resolves to an array of `JarvisProduct`
 */
export const getTariffs = async (): Promise<JarvisProduct[]> => {
    const url = isStagingEnv() ? getUrl(Domains.ST + Endpoints.JARVIS_GET_TARIFFS) : getUrl(Domains.VF + Endpoints.JARVIS_GET_TARIFFS);

    const payload: ProductDetailsRequest = {
        ids: Object.values(getCMSConfig().tariffIds.kdg),
        productType: ProductType.CABLE,
    };

    const { body } = await requestJson<ProductDetailsResponse>(url, {
        ...defaultOptions,
        method: 'POST',
        body: JSON.stringify(payload),
    });

    return body?.products || [];
};

/**
 * Get valid address
 *
 * @param isLoggedIn - If customer is logged in
 * @param address - Users address to check
 * @param gimliState - The gimli APIstate
 * @throws {ApiErrorResponseWithCode} - if the server returns with error
 * @returns A Promise which resolves to an array of `Address`
 */
export const getValidAddresses = async (isLoggedIn: boolean, address?: Partial<Address>, gimliState?: string): Promise<Address[]> => {
    const url = isStagingEnv() ? getUrl(Domains.ST + Endpoints.JARVIS_VALIDATE_ADDRESS)
        : getUrl(Domains.VF + Endpoints.JARVIS_VALIDATE_ADDRESS);
    const payload: JarvisAddressValidationRequest = {
        address,
        sessionInfo: {
            gimliState,
            isLoggedIn,
        },
    };

    const { body } = await requestJson<JarvisAddressValidationResponse>(url, {
        ...defaultOptions,
        method: 'POST',
        body: JSON.stringify(payload),
    });

    return body?.addresses || [];
};

/**
 * Delete address from state
 *
 * @param gimliState - The gimli APIstate
 * @throws {ApiErrorResponseWithCode} - if the server returns with error
 */
export const deleteAddressState = async (gimliState: string): Promise<void> => {
    const url = isStagingEnv() ? getUrl(Domains.ST + Endpoints.JARVIS_DELETE_ADDRESS + gimliState) :
        getUrl(Domains.VF + Endpoints.JARVIS_DELETE_ADDRESS + gimliState);

    await requestHtml(url, {
        ...defaultOptions,
        method: 'DELETE',
    });

    return;
};

/**
 * checkAddress
 * Check address availability for selected tariff
 *
 * @param selectedTariffId - Selected tariff by user
 * @param isLoggedIn - If customer is logged in
 * @param address - Users address to check
 * @param gimliState - The gimli APIstate
 * @throws {ApiErrorResponseWithCode} - if the server returns with error
 * @returns A Promise which resolves to a `CableAvcResponse
 */
export const checkAddress = async (
    selectedTariffId: string,
    isLoggedIn: boolean,
    address?: Partial<Address>,
    gimliState?: string,
): Promise<JarvisAvcResponse> => {
    const url = isStagingEnv() ? getUrl(Domains.ST + Endpoints.JARVIS_CHECK_AVAILABILITY) :
        getUrl(Domains.VF + Endpoints.JARVIS_CHECK_AVAILABILITY);
    const payload: JarvisAvcRequest = {
        address,
        selected: selectedTariffId,
        ids: getAllTariffIds(),
        sessionInfo: {
            gimliState,
            isLoggedIn,
        },
    };

    const { body } = await requestJson<JarvisAvcResponse>(url, {
        ...defaultOptions,
        method: 'POST',
        body: JSON.stringify(payload),
    });

    if (!body) {
        throw new Error('Empty body');
    }

    return body;
};

/**
 * Get alternative tariff suggestion from Jarvis
 *
 * @param address - Checked address
 * @param captchaToken - The verified captcha token
 * @throws {ApiErrorResponseWithCode} - if the server returns with error
 * @returns A Promise which resolves to an alternative Jarvis response
 */
export const getAlternativeAvailability = async (
    address: Address,
    captchaToken: string,
    zpApiState?: string,
): Promise<JarvisAlternativeResponse | null> => {
    const vpCaptchaQaAuthorization = getCookie(Cookies.VP_CAPTCHA_QA_AUTHORIZATION) ?? undefined;
    const url = isStagingEnv() ? getUrl(Domains.ST + Endpoints.JARVIS_GET_ALTERNATIVE_PRODUCT) :
        getUrl(Domains.VF + Endpoints.JARVIS_GET_ALTERNATIVE_PRODUCT);
    const payload: JarvisAlternativeRequest = {
        address,
        sessionInfo: {
            zpApiState,
            vpCaptchaQaAuthorization,
        },
        ...captchaToken && {
            captchaToken,
        },
    };

    const { body } = await requestJson<JarvisAlternativeResponse>(url, {
        ...defaultOptions,
        method: 'POST',
        body: JSON.stringify(payload),
    });

    return body;
};
